"use client";

import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { ArrowLeft, Plus, Trash2, Save, Loader2, MessageSquare } from "lucide-react";
import { Badge } from "../../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { MultiSelectDropdown } from "../../ui/multi-select-dropdown";
import { DocumentManager } from "../../planning/DocumentManager";
import { getCompanyName } from "@/lib/textron-data-processor";
import { RadioGroup, RadioGroupItem } from "../../ui/radio-group";
import { ReviewComment } from "../../shared/ReviewComment";

interface RelatedPartyDisclosureTailoringQuestionsProps {
  onBack?: () => void;
}

interface RelatedPartyRow {
  "Name of Party": string;
  "Nature of Relationship": string;
  "Has a transaction occurred in current period?": string;
  "Nature of transactions": string;
  "Workspace": string[]; // Array for multi-select
  "RoMM": string;
}

const NATURE_OF_RELATIONSHIP_OPTIONS = [
  "(a) Entities forming part of the same group (includes holding companies, subsidiaries and fellow subsidiaries)",
  "(b) Associate, investor, joint venture, venturer with reporting entity",
  "(c) Individuals or their relatives having significant influence or control over the reporting entity",
  "(d) KMP and relatives",
  "(e) enterprises over which any person described in (c) or (d) is able to exercise significant influence. This includes enterprises owned by directors or major shareholders of the reporting enterprise and enterprises that have a member of key management in common with the reporting enterprise.",
];

export default function RelatedPartyDisclosureTailoringQuestions({
  onBack,
}: RelatedPartyDisclosureTailoringQuestionsProps) {
  const [companyName, setCompanyName] = useState<string>("");
  const [tableRows, setTableRows] = useState<RelatedPartyRow[]>([]);
  const [workspaceOptions, setWorkspaceOptions] = useState<string[]>([]);
  const [loadingWorkspaces, setLoadingWorkspaces] = useState(false);
  const [allRomms, setAllRomms] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [questionAnswers, setQuestionAnswers] = useState<Record<string, string>>({});
  const [questionDocuments, setQuestionDocuments] = useState<Record<string, any[]>>({});
  const [questionTextResponses, setQuestionTextResponses] = useState<Record<string, any>>({});
  const [reviewCommentQuestionId, setReviewCommentQuestionId] = useState<string | null>(null);

  const AREA_NAME = "Related Party Disclosure";

  // Risk assessment hierarchy (higher number = higher risk)
  const RISK_HIERARCHY: Record<string, number> = {
    "Significant": 4,
    "Higher": 3,
    "Lower": 2,
    "NRPMM": 1,
  };

  // Get risk level number for comparison
  const getRiskLevel = useCallback((assessment: string): number => {
    const normalized = assessment.trim();
    return RISK_HIERARCHY[normalized] || 0;
  }, []);

  // Find highest assessed risk for given workspaces
  const getHighestRiskForWorkspaces = useCallback((workspaces: string[]): string => {
    if (!workspaces || workspaces.length === 0 || allRomms.length === 0) {
      return "";
    }

    let highestRisk = "";
    let highestLevel = 0;

    allRomms.forEach((romm) => {
      const rommWorkspace = romm.workspace || "";
      const rommWorkspaceArray = Array.isArray(rommWorkspace)
        ? rommWorkspace
        : rommWorkspace
        ? [rommWorkspace]
        : [];

      // Check if this RoMM matches any of the selected workspaces
      const matchesWorkspace = workspaces.some((ws) => rommWorkspaceArray.includes(ws));

      if (matchesWorkspace) {
        const assessment = romm.assesment || romm.assessment || "";
        const riskLevel = getRiskLevel(assessment);

        if (riskLevel > highestLevel) {
          highestLevel = riskLevel;
          highestRisk = assessment;
        }
      }
    });

    return highestRisk;
  }, [allRomms, getRiskLevel]);

  // Load company name
  useEffect(() => {
    const loadCompanyName = async () => {
      try {
        const name = await getCompanyName("combined");
        setCompanyName(name || "ABC Private Limited");

      } catch (error) {
        console.warn('Failed to load company name, using fallback');
        setCompanyName("ABC Private Limited");
      }
    };
    loadCompanyName();
  }, []);

  // Load Execution workspaces from Sections.json
  useEffect(() => {
    const loadWorkspaces = async () => {
      setLoadingWorkspaces(true);
      try {
        if ((window as any).sharePointAPI?.downloadSectionsJson) {
          const result = await (window as any).sharePointAPI.downloadSectionsJson();
          if (result?.success && result?.data?.Execution) {
            const executionWorkspaces = Object.keys(result.data.Execution);
            setWorkspaceOptions(executionWorkspaces.sort());
          }
        }
      } catch (error) {
        console.error("Failed to load workspaces from Sections.json:", error);
      } finally {
        setLoadingWorkspaces(false);
      }
    };
    loadWorkspaces();
  }, []);

  // Load all RoMMs from Libraries_Romm.json
  useEffect(() => {
    const loadRomms = async () => {
      try {
        if ((window as any).sharePointAPI?.readRommLibrary) {
          const result = await (window as any).sharePointAPI.readRommLibrary();
          if (result?.success && result?.data?.romm_library) {
            const romms = Array.isArray(result.data.romm_library)
              ? result.data.romm_library
              : [];
            setAllRomms(romms);
          }
        }
      } catch (error) {
        console.error("Failed to load RoMMs from Libraries_Romm.json:", error);
      }
    };
    loadRomms();
  }, []);

  // Auto-update RoMM column when RoMMs are first loaded (for existing rows with workspaces)
  useEffect(() => {
    if (allRomms.length === 0) return;

    setTableRows((prevRows) => {
      let hasChanges = false;
      const newRows = prevRows.map((row) => {
        const workspaces = row["Workspace"] || [];
        if (workspaces.length > 0) {
          const highestRisk = getHighestRiskForWorkspaces(workspaces);
          // Always update to ensure it's correct even if manually changed
          if (row["RoMM"] !== highestRisk) {
            hasChanges = true;
            return {
              ...row,
              "RoMM": highestRisk,
            };
          }
        } else if (row["RoMM"]) {
          // Clear RoMM if no workspaces selected
          hasChanges = true;
          return {
            ...row,
            "RoMM": "",
          };
        }
        return row;
      });
      
      return hasChanges ? newRows : prevRows;
    });
  }, [allRomms, getHighestRiskForWorkspaces]); // Re-run when RoMMs data changes

  // Load saved data
  useEffect(() => {
    const loadData = async () => {
      if (!companyName) {
        setLoading(false);
        return;
      }

      try {
        // Load directly from Execution_TailoringQuestions_Responses.json
        if ((window as any).sharePointAPI?.downloadClientJson) {
          const result = await (window as any).sharePointAPI.downloadClientJson('Execution_TailoringQuestions_Responses.json');

          if (result?.success && result?.data) {
            // Get data for "Related Party Disclosure" area
            const areaData = result.data[AREA_NAME];
            
            if (areaData) {
              const savedData = areaData["related-party-list"];

              if (savedData?.table) {
                const savedTable = savedData.table;
                if (savedTable.columns && savedTable.rows && savedTable.rows.length > 0) {
                  // Convert saved table rows to RelatedPartyRow format
                  const loadedRows: RelatedPartyRow[] = savedTable.rows.map((row: any) => ({
                    "Name of Party": row["Name of Party"] || "",
                    "Nature of Relationship": row["Nature of Relationship"] || "",
                    "Has a transaction occurred in current period?": row["Has a transaction occurred in current period?"] || "",
                    "Nature of transactions": row["Nature of transactions"] || "",
                    "Workspace": Array.isArray(row["Workspace"]) ? row["Workspace"] : (row["Workspace"] ? [row["Workspace"]] : []),
                    "RoMM": row["RoMM"] || "",
                  }));
                  setTableRows(loadedRows);
                }
              }

              // Load question answers
              const loadedAnswers: Record<string, string> = {};
              const loadedDocuments: Record<string, any[]> = {};
              const loadedTextResponses: Record<string, any> = {};

              // Load transactions outside normal course question
              if (areaData["transactions-outside-normal"]) {
                const data = areaData["transactions-outside-normal"];
                if (data.selectedOption) loadedAnswers["transactions-outside-normal"] = data.selectedOption;
                if (data.documents) loadedDocuments["transactions-outside-normal"] = Array.isArray(data.documents) ? data.documents.map((doc: string | any) => {
                  if (typeof doc === 'string') {
                    return { id: `doc_${doc}`, type: 'linked', name: doc };
                  }
                  return doc;
                }) : [];
                if (data.textResponse) loadedTextResponses["transactions-outside-normal"] = data.textResponse;
              }

              // Load transactions outside normal course document (separate question ID)
              if (areaData["transactions-outside-normal-doc"]) {
                const data = areaData["transactions-outside-normal-doc"];
                if (data.documents) loadedDocuments["transactions-outside-normal-doc"] = Array.isArray(data.documents) ? data.documents.map((doc: string | any) => {
                  if (typeof doc === 'string') {
                    return { id: `doc_${doc}`, type: 'linked', name: doc };
                  }
                  return doc;
                }) : [];
                if (data.textResponse) loadedTextResponses["transactions-outside-normal-doc"] = data.textResponse;
              }

              // Load identification question
              if (areaData["unidentified-related-parties"]) {
                const data = areaData["unidentified-related-parties"];
                if (data.selectedOption) loadedAnswers["unidentified-related-parties"] = data.selectedOption;
                if (data.documents) loadedDocuments["unidentified-related-parties"] = Array.isArray(data.documents) ? data.documents.map((doc: string | any) => {
                  if (typeof doc === 'string') {
                    return { id: `doc_${doc}`, type: 'linked', name: doc };
                  }
                  return doc;
                }) : [];
                if (data.textResponse) loadedTextResponses["unidentified-related-parties"] = data.textResponse;
              }

              setQuestionAnswers(loadedAnswers);
              setQuestionDocuments(loadedDocuments);
              setQuestionTextResponses(loadedTextResponses);
            }
          }
        }
      } catch (error) {
        console.error("Failed to load tailoring questions responses:", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [companyName]);

  const addRow = () => {
    setTableRows([
      ...tableRows,
      {
        "Name of Party": "",
        "Nature of Relationship": "",
        "Has a transaction occurred in current period?": "",
        "Nature of transactions": "",
        "Workspace": [],
        "RoMM": "",
      },
    ]);
  };

  const removeRow = (index: number) => {
    setTableRows(tableRows.filter((_, i) => i !== index));
  };

  const updateRow = (index: number, field: keyof RelatedPartyRow, value: string | string[]) => {
    const updated = [...tableRows];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    
    // If Workspace field is updated, automatically calculate and update RoMM
    if (field === "Workspace" && allRomms.length > 0) {
      const workspaces = Array.isArray(value) ? value : [value];
      const highestRisk = getHighestRiskForWorkspaces(workspaces);
      updated[index]["RoMM"] = highestRisk;
    }
    
    setTableRows(updated);
  };

  const handleSave = useCallback(async () => {
    if (!companyName) {
      setSaveError("Company name not available");
      setSaveStatus('error');
      return;
    }

    setSaving(true);
    setSaveStatus('saving');
    setSaveError(null);

    try {
      const tableColumns = [
        "Name of Party",
        "Nature of Relationship",
        "Has a transaction occurred in current period?",
        "Nature of transactions",
        "Workspace",
        "RoMM",
      ];

      const tableRowsToSave = tableRows.map((row) => ({
        "Name of Party": row["Name of Party"],
        "Nature of Relationship": row["Nature of Relationship"],
        "Has a transaction occurred in current period?": row["Has a transaction occurred in current period?"],
        "Nature of transactions": row["Nature of transactions"],
        "Workspace": row["Workspace"], // Keep as array
        "RoMM": row["RoMM"],
      }));

      const responsesToSave: Record<string, any> = {
        "related-party-list": {
          question: "Document the list of related parties to entity on the basis of criteria laid out in the financial reporting framework.",
          table: {
            columns: tableColumns,
            rows: tableRowsToSave,
          },
          lastUpdated: new Date().toISOString(),
          companyName,
        },
        "transactions-outside-normal": {
          selectedOption: questionAnswers["transactions-outside-normal"] || "",
          documents: questionDocuments["transactions-outside-normal"] || [],
          textResponse: questionTextResponses["transactions-outside-normal"] || null,
          lastUpdated: new Date().toISOString(),
          companyName,
        },
        "transactions-outside-normal-doc": {
          documents: questionDocuments["transactions-outside-normal-doc"] || [],
          textResponse: questionTextResponses["transactions-outside-normal-doc"] || null,
          lastUpdated: new Date().toISOString(),
          companyName,
        },
        "unidentified-related-parties": {
          selectedOption: questionAnswers["unidentified-related-parties"] || "",
          documents: questionDocuments["unidentified-related-parties"] || [],
          textResponse: questionTextResponses["unidentified-related-parties"] || null,
          lastUpdated: new Date().toISOString(),
          companyName,
        },
      };

      if (window.tailoringQuestionsAPI?.saveResponses) {
        const result = await window.tailoringQuestionsAPI.saveResponses({
          companyName,
          areaName: AREA_NAME,
          responses: responsesToSave,
        });

        if (result.success) {
          setSaveStatus('saved');
          setTimeout(() => setSaveStatus('idle'), 3000);
        } else {
          throw new Error(result.error || "Failed to save responses");
        }
      } else {
        throw new Error("Tailoring Questions API not available");
      }
    } catch (error) {
      console.error("Failed to save tailoring questions responses:", error);
      setSaveError(error instanceof Error ? error.message : "Unknown error");
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  }, [companyName, tableRows, questionAnswers, questionDocuments, questionTextResponses]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-white/60" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Related Party Disclosure - Tailoring Questions</h2>
          <p className="text-gray-400">Customize the audit program for Related Party Disclosure</p>
        </div>
        {onBack && (
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
        )}
      </div>

      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">
              Document the list of related parties to entity on the basis of criteria laid out in the financial reporting framework.
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId("RELATED_PARTY_DISCLOSURE_RELATED_PARTY_LIST")}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button type="button" onClick={addRow} className="flex items-center gap-2">
                <Plus className="h-4 w-4" /> Add Row
              </Button>
            </div>

            <div className="overflow-x-auto rounded-lg border border-white/10">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-white/10 bg-black/20">
                    <th className="p-3 text-left text-sm font-medium text-white/80">Name of Party</th>
                    <th className="p-3 text-left text-sm font-medium text-white/80 w-64">Nature of Relationship</th>
                    <th className="p-3 text-left text-sm font-medium text-white/80">Has a transaction occurred in current period?</th>
                    <th className="p-3 text-left text-sm font-medium text-white/80">Nature of transactions</th>
                    <th className="p-3 text-left text-sm font-medium text-white/80">Workspace</th>
                    <th className="p-3 text-left text-sm font-medium text-white/80">RoMM</th>
                    <th className="p-3 text-center text-sm font-medium text-white/80 w-20">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="p-8 text-center text-white/60">
                        No rows added. Click "Add Row" to add a new entry.
                      </td>
                    </tr>
                  ) : (
                    tableRows.map((row, index) => (
                      <tr key={index} className="border-b border-white/10">
                        <td className="p-3">
                          <Input
                            type="text"
                            value={row["Name of Party"]}
                            onChange={(e) => updateRow(index, "Name of Party", e.target.value)}
                            className="w-full bg-black/20 border-white/10 text-white placeholder:text-white/50"
                            placeholder="Entered by user"
                          />
                        </td>
                        <td className="p-3 w-64">
                          <Select
                            value={row["Nature of Relationship"]}
                            onValueChange={(value) => updateRow(index, "Nature of Relationship", value)}
                          >
                            <SelectTrigger className="w-full min-h-[2.5rem] h-auto [&>span]:line-clamp-none [&>span]:whitespace-normal [&>span]:break-words">
                              <SelectValue 
                                placeholder="Dropdown list given below"
                              />
                            </SelectTrigger>
                            <SelectContent className="max-w-md">
                              {NATURE_OF_RELATIONSHIP_OPTIONS.map((option) => (
                                <SelectItem 
                                  key={option} 
                                  value={option} 
                                  className="text-white whitespace-normal break-words py-2"
                                >
                                  {option}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-3">
                          <Select
                            value={row["Has a transaction occurred in current period?"]}
                            onValueChange={(value) => updateRow(index, "Has a transaction occurred in current period?", value)}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Yes/No dropdown" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Yes">Yes</SelectItem>
                              <SelectItem value="No">No</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-3">
                          <Textarea
                            value={row["Nature of transactions"]}
                            onChange={(e) => updateRow(index, "Nature of transactions", e.target.value)}
                            className="w-full bg-black/20 border-white/10 text-white placeholder:text-white/50 min-h-[60px]"
                            placeholder="Entered by user. User can add multiple transaction nature here under the same party."
                          />
                        </td>
                        <td className="p-3">
                          <MultiSelectDropdown
                            options={workspaceOptions}
                            value={row["Workspace"] || []}
                            onChange={(value) => updateRow(index, "Workspace", value)}
                            placeholder="Select workspaces..."
                            loading={loadingWorkspaces}
                            disabled={loadingWorkspaces}
                          />
                        </td>
                        <td className="p-3">
                          <Input
                            type="text"
                            value={row["RoMM"]}
                            onChange={(e) => updateRow(index, "RoMM", e.target.value)}
                            className="w-full bg-black/20 border-white/10 text-white placeholder:text-white/50"
                            placeholder="Auto-populated from selected workspaces"
                            readOnly
                            title="This field is automatically populated with the highest assessed risk from the selected workspaces"
                          />
                        </td>
                        <td className="p-3 text-center">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeRow(index)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Transactions Outside Entity's normal course of business */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Transactions Outside Entity's normal course of business</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId("RELATED_PARTY_DISCLOSURE_TRANSACTIONS_OUTSIDE_NORMAL")}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium text-white">
              Have there been any significant transactions with related parties outside the entity's normal course of business?
            </Label>
            <RadioGroup
              value={questionAnswers["transactions-outside-normal"] || ""}
              onValueChange={(value) => setQuestionAnswers((prev) => ({ ...prev, "transactions-outside-normal": value }))}
              className="ml-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Yes" id="transactions-outside-normal-yes" className="border-white/20 text-[#4da3ff]" />
                <Label htmlFor="transactions-outside-normal-yes" className="text-sm text-white/80 cursor-pointer">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="No" id="transactions-outside-normal-no" className="border-white/20 text-[#4da3ff]" />
                <Label htmlFor="transactions-outside-normal-no" className="text-sm text-white/80 cursor-pointer">No</Label>
              </div>
            </RadioGroup>

            {questionAnswers["transactions-outside-normal"] === "Yes" && (
              <div className="ml-8 mt-4 space-y-4 border-l-2 border-l-[#4da3ff]/30 pl-4">
                <div className="text-sm text-white/80">
                  Document our considerations in evaluating the nature of the transactions by:
                </div>
                <div className="space-y-2 ml-4">
                  <div className="text-sm text-white/90">
                    1. Inspect the underlying contracts or agreements, if any, and evaluating whether
                  </div>
                  <div className="ml-4 space-y-1 text-sm text-white/80">
                    <div>a. The business rationale (or lack thereof) of the transactions suggests that they may have been entered into to engage in fraudulent financial reporting or to conceal misappropriation of assets;</div>
                    <div>b. The terms of the transactions are consistent with management's explanations;</div>
                    <div>c. The transactions have been appropriately accounted for and disclosed in accordance with the applicable financial reporting framework</div>
                  </div>
                  <div className="text-sm text-white/90 mt-3">
                    2. Obtain audit evidence that the transactions have been appropriately authorised and approved.
                  </div>
                </div>
                <div className="mt-4">
                  <DocumentManager
                    sectionKey={`execution-tailoring:${AREA_NAME}`}
                    itemId="transactions-outside-normal-doc"
                    itemQuestion="Document our considerations in evaluating the nature of transactions outside entity's normal course of business"
                    itemTextResponse={questionTextResponses["transactions-outside-normal-doc"]}
                    itemDocuments={questionDocuments["transactions-outside-normal-doc"]?.map((doc: string | any) => {
                      if (typeof doc === 'string') {
                        return {
                          id: `doc_${doc}`,
                          type: 'linked' as const,
                          name: doc
                        };
                      }
                      return doc;
                    }) || []}
                    onDocumentsChange={(docs) => setQuestionDocuments((prev) => ({ ...prev, "transactions-outside-normal-doc": docs }))}
                    onTextResponseChange={(textResp) => setQuestionTextResponses((prev) => ({ ...prev, "transactions-outside-normal-doc": textResp }))}
                    disabled={false}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Identification of Previously Unidentified or Undisclosed Related Parties */}
      <Card className="border-white/10 bg-black/40">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white">Identification of Previously Unidentified or Undisclosed Related Parties or Significant Related Party Transactions</CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setReviewCommentQuestionId("RELATED_PARTY_DISCLOSURE_UNIDENTIFIED_PARTIES")}
              className="flex-shrink-0 p-1 h-7 w-7 text-yellow-400 hover:text-yellow-300 hover:bg-white/10"
              title="Review Comment"
            >
              <MessageSquare className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <Label className="text-sm font-medium text-white">
              Have we identified arrangements or information that suggests the existence of related party relationships or transactions that management has not previously identified or disclosed to us?
            </Label>
            <RadioGroup
              value={questionAnswers["unidentified-related-parties"] || ""}
              onValueChange={(value) => setQuestionAnswers((prev) => ({ ...prev, "unidentified-related-parties": value }))}
              className="ml-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="Yes" id="unidentified-related-parties-yes" className="border-white/20 text-[#4da3ff]" />
                <Label htmlFor="unidentified-related-parties-yes" className="text-sm text-white/80 cursor-pointer">Yes</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="No" id="unidentified-related-parties-no" className="border-white/20 text-[#4da3ff]" />
                <Label htmlFor="unidentified-related-parties-no" className="text-sm text-white/80 cursor-pointer">No</Label>
              </div>
            </RadioGroup>

            {questionAnswers["unidentified-related-parties"] === "Yes" && (
              <div className="ml-8 mt-4 space-y-4 border-l-2 border-l-[#4da3ff]/30 pl-4">
                <div className="text-sm text-white/80">
                  Document our procedures performed for the following:
                </div>
                <div className="space-y-2 ml-4">
                  <div className="text-sm text-white/90">
                    1. Promptly communicate the relevant information to the other members of the engagement team; (SA 550:A35)
                  </div>
                  <div className="text-sm text-white/90 mt-3">
                    2. Where the applicable financial reporting framework establishes related party requirements:
                  </div>
                  <div className="ml-4 space-y-1 text-sm text-white/80">
                    <div>a. Request management to identify all transactions with the newly identified related parties for the auditor's further evaluation; and</div>
                    <div>b. Inquire as to why the entity's controls over related party relationships and transactions failed to enable the identification or disclosure of the related party relationships or transactions;</div>
                  </div>
                  <div className="text-sm text-white/90 mt-3">
                    3. Perform appropriate substantive audit procedures relating to such newly identified related parties or significant related party transactions; (SA 550:A36)
                  </div>
                  <div className="text-sm text-white/90 mt-3">
                    4. Reconsider the risk that other related parties or significant related party transactions may exist that management has not previously identified or disclosed to the auditor, and perform additional audit procedures as necessary; and
                  </div>
                  <div className="text-sm text-white/90 mt-3">
                    5. If the non-disclosure by management appears intentional (and therefore indicative of a risk of material misstatement due to fraud), evaluate the implications for the audit. (SA 550:A37)
                  </div>
                </div>
                <div className="mt-4">
                  <DocumentManager
                    sectionKey={`execution-tailoring:${AREA_NAME}`}
                    itemId="unidentified-related-parties"
                    itemQuestion="Document our procedures performed for identification of previously unidentified or undisclosed related parties or significant related party transactions"
                    itemTextResponse={questionTextResponses["unidentified-related-parties"]}
                    itemDocuments={questionDocuments["unidentified-related-parties"]?.map((doc: string | any) => {
                      if (typeof doc === 'string') {
                        return {
                          id: `doc_${doc}`,
                          type: 'linked' as const,
                          name: doc
                        };
                      }
                      return doc;
                    }) || []}
                    onDocumentsChange={(docs) => setQuestionDocuments((prev) => ({ ...prev, "unidentified-related-parties": docs }))}
                    onTextResponseChange={(textResp) => setQuestionTextResponses((prev) => ({ ...prev, "unidentified-related-parties": textResp }))}
                    disabled={false}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end items-center gap-4 pt-6">
        {saveStatus === 'saved' && (
          <Badge variant="outline" className="border-green-600 text-green-600">
            Saved successfully
          </Badge>
        )}
        {saveStatus === 'error' && saveError && (
          <Badge variant="destructive" className="text-red-600">
            Error: {saveError}
          </Badge>
        )}
        <Button
          onClick={handleSave}
          className="flex items-center gap-2"
          disabled={saving || !companyName}
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save Responses
            </>
          )}
        </Button>
      </div>

      {/* Review Comment Modal */}
      {reviewCommentQuestionId && (
        <ReviewComment
          questionId={reviewCommentQuestionId}
          questionText={(() => {
            const questionMap: Record<string, string> = {
              "RELATED_PARTY_DISCLOSURE_RELATED_PARTY_LIST": "Document the list of related parties to entity on the basis of criteria laid out in the financial reporting framework.",
              "RELATED_PARTY_DISCLOSURE_TRANSACTIONS_OUTSIDE_NORMAL": "Transactions Outside Entity's normal course of business",
              "RELATED_PARTY_DISCLOSURE_UNIDENTIFIED_PARTIES": "Identification of Previously Unidentified or Undisclosed Related Parties or Significant Related Party Transactions",
            };
            return questionMap[reviewCommentQuestionId] || reviewCommentQuestionId;
          })()}
          isOpen={!!reviewCommentQuestionId}
          onClose={() => setReviewCommentQuestionId(null)}
        />
      )}
    </div>
  );
}
